import styles from './sessions.module.css';
/**
 * Student Session Card
 *
 * Layout: world-preview slot on the left, body on the right with status badge,
 * title, time, team and join button. Variant + Enter button state are driven
 * by session.status (server-authoritative):
 *   SCHEDULED → upcoming, button disabled (lobby not yet open)
 *   RUNNING   → live, button enabled
 *   FINISHED  → past
 *
 * Start time displayed is the lobby-open time: startTimeUtc less
 * LOBBY_PRE_MINUTES, when the game server makes the session's match.
 */

import { Photo } from '../../components/photo';
import { Pill } from '../../components/pill';
import { Card } from '../../components/card';
import { Button } from '../../components/button';
import { useMemo } from 'react';
import { useNowSeconds } from '../../utils/use-now-seconds';
import { useTranslation } from 'react-i18next';
import type { StudentSessionView } from '../../api/student/sessions/types';
import { LOBBY_PRE_MINUTES } from '../../api/_shared/session-timing';
import { SESSION_LOOK } from './session-look';
import { sessionShown } from '../../api/student/session-attendance';

type CardVariant = 'upcoming' | 'past';

interface StudentSessionCardProps {
  session: StudentSessionView;
  variant: CardVariant;
  launching?: boolean;
  onLaunch?: () => void;
}

export function StudentSessionCard({ session, variant, launching, onLaunch }: StudentSessionCardProps) {
  const { t, i18n } = useTranslation();

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const startLobbyMs = (session.startTimeUtc - LOBBY_PRE_MINUTES * 60) * 1000;
  const startLabel = formatter.format(new Date(startLobbyMs));

  // All countdowns share the same wall clock.
  const now = useNowSeconds() * 1000;

  const startsInLabel = (() => {
    const ms = startLobbyMs - now;
    if (ms <= 0) return null;
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return t('student.home.upNext.startsInMinutes', { minutes: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('student.home.upNext.startsInHours', { hours, minutes: mins % 60 });
    const days = Math.floor(hours / 24);
    return t('student.home.upNext.startsInDays', { days });
  })();

  const isLive = session.status === 'RUNNING';
  const canJoin = isLive;

  const attendedSet = useMemo(
    () => new Set(session.participantNakamaIds ?? []),
    [session.participantNakamaIds],
  );
  const knowsAttendance = variant === 'past' && !session.matchDataMissing;

  // Its pill: where it stands for the student (session-look.ts).
  const look = SESSION_LOOK[sessionShown(session.status, session.participated)];

  return (
    <Card as="article" padded={false} className={`${styles.srow}`}>
      <Photo className={`${styles.srow__art}`}>
        <Pill tone={look.tone}>{t(look.key)}</Pill>
      </Photo>

      <div className={`${styles.srow__body}`}>
        <h3 className={`${styles.srow__title}`}>{session.world.name}</h3>
        <p className={`${styles.srow__meta}`}>
          {session.game.name} · <span className={`${styles.srow__team}`}>{session.cohort.name}</span>
        </p>
        <p className={`${styles.srow__meta}`}>
          <strong>{t('student.sessions.card.startsAt')}:</strong> {startLabel}
        </p>
        {startsInLabel && variant === 'upcoming' && !isLive && (
          <p className={`${styles.srow__countdown}`}>⏱ {startsInLabel}</p>
        )}

        {session.teamRoster.length > 0 && (
          <ul className={`${styles.roster}`}>
            {session.teamRoster.map((member) => {
              const attended = attendedSet.has(member.nakamaCustomId);
              return (
                <li key={member.userId} className={`${styles.roster__item}`}>
                  <span>{member.displayName.trim().split(/\s+/)[0]}</span>
                  {knowsAttendance && (
                    <span
                      className={attended ? styles.roster__statusAttended : styles.roster__statusMissed}
                      aria-label={attended ? t('student.sessions.card.attended') : t('student.sessions.card.notAttended')}
                    >
                      {attended ? '✓' : '✗'}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {variant === 'past' && session.matchDataMissing && (
          <p className={`${styles.srow__note}`}>{t('student.sessions.card.matchDataMissing')}</p>
        )}

        {variant === 'upcoming' && onLaunch && (
          <Button
            variant="gold"
            className={`${styles.srow__launch}`}
            onClick={onLaunch}
            disabled={!canJoin || !!launching}
            title={canJoin ? undefined : t('student.sessions.card.joinDisabledHint')}
          >
            🎮 {launching ? t('common.loading') : t('student.sessions.enterGame')}
          </Button>
        )}
      </div>
    </Card>
  );
}
